import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function QuestionField({ q, value, onChange, colors }) {
  const base = { background: colors.inputBg, color: colors.heading, borderColor: colors.border };
  const testId = `survey-q-${q.id}`;

  if (q.type === "rating") {
    return (
      <div className="flex gap-2" data-testid={testId}>
        {[1, 2, 3, 4, 5].map((r) => (
          <button
            key={r}
            type="button"
            onClick={() => onChange(r)}
            data-testid={`${testId}-${r}`}
            className="w-10 h-10 rounded-full text-sm font-semibold transition-colors"
            style={{
              background: value === r ? colors.accent : colors.chipBg,
              color: value === r ? "#fff" : colors.heading,
            }}
          >
            {r}
          </button>
        ))}
      </div>
    );
  }
  if (q.type === "yesno") {
    return (
      <div className="flex gap-2" data-testid={testId}>
        {["Yes", "No"].map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt)}
            data-testid={`${testId}-${opt.toLowerCase()}`}
            className="px-5 py-2 rounded-full text-sm font-semibold transition-colors"
            style={{ background: value === opt ? colors.accent : colors.chipBg, color: value === opt ? "#fff" : colors.heading }}
          >
            {opt}
          </button>
        ))}
      </div>
    );
  }
  if (q.type === "select") {
    const isOther = q.has_other && value !== undefined && !q.options.includes(value);
    return (
      <div className="space-y-2">
        <select
          value={isOther ? "__other__" : value || ""}
          onChange={(e) => onChange(e.target.value === "__other__" ? "Other: " : e.target.value)}
          data-testid={testId}
          className="w-full px-3 py-2 rounded-md border text-sm"
          style={base}
        >
          <option value="">Select...</option>
          {q.options.map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
          {q.has_other && <option value="__other__">Other (please specify)</option>}
        </select>
        {isOther && (
          <Input
            value={value.replace(/^Other: /, "")}
            onChange={(e) => onChange(`Other: ${e.target.value}`)}
            placeholder="Please specify"
            data-testid={`${testId}-other`}
            style={base}
          />
        )}
      </div>
    );
  }
  if (q.type === "checkbox_group") {
    const selected = Array.isArray(value) ? value : [];
    return (
      <div className="grid sm:grid-cols-2 gap-2" data-testid={testId}>
        {q.options.map((o) => (
          <label key={o} className="flex items-center gap-2 text-sm cursor-pointer" style={{ color: colors.heading }}>
            <input
              type="checkbox"
              checked={selected.includes(o)}
              onChange={(e) => onChange(e.target.checked ? [...selected, o] : selected.filter((s) => s !== o))}
            />
            {o}
          </label>
        ))}
      </div>
    );
  }
  if (q.type === "textarea") {
    return <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={3} data-testid={testId} style={base} />;
  }
  return (
    <Input
      type={q.type === "email" ? "email" : "text"}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      style={base}
    />
  );
}

export function UniversalSurveyForm({ courseId, token, onSubmitted, isDarkMode, defaultEmail }) {
  const [template, setTemplate] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const colors = {
    heading: isDarkMode ? "#ffffff" : "#092036",
    text: isDarkMode ? "#94a3b8" : "#4b5563",
    accent: "#FF6B35",
    border: isDarkMode ? "#1e3a5f" : "#e5e7eb",
    inputBg: isDarkMode ? "#0a1628" : "#ffffff",
    chipBg: isDarkMode ? "#1e3a5f" : "#e5e7eb",
  };

  useEffect(() => {
    axios.get(`${API}/survey-template`).then((res) => {
      setTemplate(res.data);
      if (defaultEmail) {
        const emailQ = res.data.questions.find((q) => q.type === "email");
        if (emailQ) setAnswers((a) => ({ ...a, [emailQ.id]: defaultEmail }));
      }
    }).catch(() => toast.error("Could not load survey"));
  }, [defaultEmail]);

  const handleSubmit = async () => {
    const missing = template.questions.find((q) => q.required && (answers[q.id] === undefined || answers[q.id] === "" || answers[q.id] === "Other: "));
    if (missing) {
      toast.error(`Please answer: ${missing.text}`);
      document.querySelector(`[data-testid="survey-q-${missing.id}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/survey-submit/${courseId}`, { answers }, { headers: { Authorization: `Bearer ${token}` } });
      onSubmitted?.(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit survey");
    } finally {
      setSubmitting(false);
    }
  };

  if (!template) {
    return (
      <div className="flex justify-center py-12" data-testid="survey-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: colors.accent }}></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="universal-survey-form">
      {template.questions.map((q, idx) => (
        <div key={q.id}>
          <p className="text-sm font-medium mb-2" style={{ color: colors.heading }}>
            {idx + 1}. {q.text} {q.required && <span style={{ color: colors.accent }}>*</span>}
          </p>
          <QuestionField q={q} value={answers[q.id]} onChange={(v) => setAnswers((a) => ({ ...a, [q.id]: v }))} colors={colors} />
        </div>
      ))}
      <Button
        onClick={handleSubmit}
        disabled={submitting}
        className="w-full py-5 text-base font-semibold text-white"
        style={{ background: colors.accent }}
        data-testid="ce-submit-survey"
      >
        {submitting ? "Submitting..." : "Submit & Get Certificate"}
      </Button>
    </div>
  );
}
